'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslations, useLocale } from 'next-intl'
import { addDays, differenceInDays } from 'date-fns'
import { formatDayMonth, formatDayMonthYear, toLocalDate } from '@/lib/date-display'
import { CalendarIcon, Users, Search, Minus, Plus, Loader2, Tag, X, Check, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { PropertyDetailData } from '@/services/property-detail'
import {
  evaluateCouponEligibility,
  type CouponEligibility,
  type CouponPreview,
} from '@/services/coupon-shared'
import { CouponBanner } from '../../../components/CouponBanner'

import type { AvailabilityMap, SessionData } from './PropertyDetailClient'

interface BookingSidebarProps {
  property: PropertyDetailData
  tenantId: string
  onAvailabilityLoaded?: (
    availability: AvailabilityMap,
    dates: {
      checkIn: string
      checkOut: string
      adults: number
      children: number
      nights: number
      couponCode?: string
      discount?: { discountType: string; discountValue: number }
      allowedRoomTypeIds?: string[]
    },
  ) => void
  sessionData?: SessionData
  /** Promo code to prefill and validate on mount when there is no session (tracked campaign link coupon). */
  initialCouponCode?: string
}

/**
 * Fetch availability for THIS property.
 *
 * GH #1660 — sends the property **id** the server already resolved tenant-scoped
 * for this page, never the code. A bare code is only unique per tenant, and the
 * public endpoint that used to accept one resolved it with a LIKE pattern.
 */
async function fetchAvailability(
  propertyId: string,
  checkIn: string,
  checkOut: string,
  adults: number,
  children: number
): Promise<{
  success: boolean
  availability?: AvailabilityMap
  error?: string
}> {
  const res = await fetch(
    `/api/booking/property-availability?propertyId=${encodeURIComponent(propertyId)}&checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}`
  )
  return res.json()
}

export function BookingSidebar({ property, tenantId, onAvailabilityLoaded, sessionData, initialCouponCode }: BookingSidebarProps) {
  const t = useTranslations('booking.propertyDetail')
  const tSearch = useTranslations('booking.search')
  const locale = useLocale()

  const [checkIn, setCheckIn] = useState<string | undefined>(sessionData?.checkIn)
  const [checkOut, setCheckOut] = useState<string | undefined>(sessionData?.checkOut)
  const [adults, setAdults] = useState(sessionData?.adults ?? 2)
  const [children, setChildren] = useState(sessionData?.children ?? 0)
  const [isDateOpen, setIsDateOpen] = useState(false)
  const [isGuestOpen, setIsGuestOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [availability, setAvailability] = useState<AvailabilityMap | null>(null)
  const [searchedDates, setSearchedDates] = useState<{ checkIn: string; checkOut: string; adults: number; children: number } | null>(null)

  // Mobile sheet
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false)

  // Lock body scroll when mobile sheet is open
  useEffect(() => {
    if (isMobileSheetOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
    return undefined
  }, [isMobileSheetOpen])

  // Track whether we've already auto-searched from session
  const hasAutoSearched = useRef(false)

  // Promo code
  const [couponCode, setCouponCode] = useState(sessionData?.couponCode || initialCouponCode || '')
  const [showPromoField, setShowPromoField] = useState(!!(sessionData?.couponCode || initialCouponCode))
  const [couponStatus, setCouponStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid' | 'not_eligible'>('idle')
  const [couponInfo, setCouponInfo] = useState<{
    discountType?: string
    discountValue?: number
    /** When set, the coupon only applies to these room types on this property */
    allowedRoomTypeIds?: string[]
  } | null>(null)
  // Full preview + eligibility for the contextual inline banner
  const [couponPreview, setCouponPreview] = useState<CouponPreview | null>(null)
  const [couponEligibility, setCouponEligibility] = useState<CouponEligibility | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const validateCouponCode = useCallback(async (code: string) => {
    if (!code || code.length < 3 || !tenantId) {
      setCouponStatus('idle')
      setCouponInfo(null)
      setCouponPreview(null)
      setCouponEligibility(null)
      return
    }
    setCouponStatus('validating')
    try {
      const res = await fetch(`/api/booking/validate-coupon?code=${encodeURIComponent(code)}&tenantId=${tenantId}`)
      const data = await res.json() as CouponPreview
      setCouponPreview(data)

      // Compute nights from current sidebar selection (or sessionData fallback)
      const start = checkIn || sessionData?.checkIn
      const end = checkOut || sessionData?.checkOut
      const nights = start && end
        ? Math.max(1, Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / 86400000))
        : undefined

      const eligibility = evaluateCouponEligibility(data, {
        propertyId: property.id,
        checkIn: start || undefined,
        checkOut: end || undefined,
        nights,
      })
      setCouponEligibility(eligibility)

      if (eligibility.eligible) {
        const propertyRoomTypes = data.roomTypesByProperty?.[property.id]
        setCouponStatus('valid')
        setCouponInfo({
          discountType: data.discountType,
          discountValue: data.discountValue,
          allowedRoomTypeIds: propertyRoomTypes && propertyRoomTypes.length > 0 ? propertyRoomTypes : undefined,
        })
      } else {
        // Distinguish "code itself is invalid/expired" from "valid but not for
        // this property/dates" — the input border colour and helper text
        // already differ between these two states.
        setCouponStatus(data.valid ? 'not_eligible' : 'invalid')
        setCouponInfo(null)
      }
    } catch {
      setCouponStatus('idle')
      setCouponInfo(null)
      setCouponPreview(null)
      setCouponEligibility(null)
    }
  }, [tenantId, property.id, checkIn, checkOut, sessionData?.checkIn, sessionData?.checkOut])

  const handleCouponChange = (value: string) => {
    const upper = value.toUpperCase()
    setCouponCode(upper)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!upper || upper.length < 3) {
      setCouponStatus('idle')
      setCouponInfo(null)
      return
    }
    debounceRef.current = setTimeout(() => validateCouponCode(upper), 500)
  }

  const handleDateSelect = (isoDate: string) => {
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(isoDate)
      setCheckOut(undefined)
      setAvailability(null)
    } else {
      const startDate = new Date(checkIn)
      const endDate = new Date(isoDate)
      if (endDate > startDate) {
        setCheckOut(isoDate)
        setIsDateOpen(false)
      } else {
        setCheckIn(isoDate)
        setCheckOut(checkIn)
        setIsDateOpen(false)
      }
    }
  }

  const handleSearch = useCallback(async () => {
    if (!checkIn || !checkOut) return
    setIsSearching(true)
    try {
      const result = await fetchAvailability(property.id, checkIn, checkOut, adults, children)
      if (result.success && result.availability) {
        const n = differenceInDays(new Date(checkOut), new Date(checkIn))
        setAvailability(result.availability)
        setSearchedDates({ checkIn, checkOut, adults, children })
        const trimmedCoupon = couponCode.trim()
        const discountData = couponStatus === 'valid' && couponInfo
          ? { discountType: couponInfo.discountType!, discountValue: couponInfo.discountValue! }
          : undefined
        onAvailabilityLoaded?.(result.availability, {
          checkIn, checkOut, adults, children,
          nights: n,
          couponCode: trimmedCoupon || undefined,
          discount: discountData,
          allowedRoomTypeIds: couponInfo?.allowedRoomTypeIds,
        })
        setIsMobileSheetOpen(false)
      }
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setIsSearching(false)
    }
  }, [checkIn, checkOut, adults, children, property.id, couponCode, couponStatus, couponInfo, onAvailabilityLoaded])

  // A coupon prefilled by a tracked campaign link (no session): validate it once
  // so the discount preview and the not-eligible messaging show as if typed.
  useEffect(() => {
    if (!sessionData && initialCouponCode && initialCouponCode.length >= 3) validateCouponCode(initialCouponCode)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-search when arriving from the search flow with session data
  useEffect(() => {
    if (!sessionData || hasAutoSearched.current || !checkIn || !checkOut) return
    hasAutoSearched.current = true

    // Resolve discount from session
    const sessionDiscount = sessionData.agencyDiscount || sessionData.guestDiscount
    if (sessionDiscount) {
      setCouponStatus('valid')
      setCouponInfo({ discountType: sessionDiscount.discountType, discountValue: sessionDiscount.discountValue })
    }

    // Fetch availability directly (avoid stale closure from handleSearch)
    ;(async () => {
      setIsSearching(true)
      try {
        const result = await fetchAvailability(property.id, checkIn, checkOut, adults, children)
        if (result.success && result.availability) {
          const n = differenceInDays(new Date(checkOut), new Date(checkIn))
          setAvailability(result.availability)
          setSearchedDates({ checkIn, checkOut, adults, children })
          const discountData = sessionDiscount
            ? { discountType: sessionDiscount.discountType, discountValue: sessionDiscount.discountValue }
            : undefined
          onAvailabilityLoaded?.(result.availability, {
            checkIn, checkOut, adults, children, nights: n,
            couponCode: sessionData.couponCode,
            discount: discountData,
          })
        }
      } catch (error) {
        console.error('Auto-search error:', error)
      } finally {
        setIsSearching(false)
      }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nights = checkIn && checkOut ? differenceInDays(new Date(checkOut), new Date(checkIn)) : 0
  const totalGuests = adults + children
  // date-fns-based (NOT Intl): this text is in the SSR HTML — Intl 'ar' output
  // differs between WebKit and Node, causing hydration mismatches on Safari.
  const fmtShort = { format: (d: string | Date) => formatDayMonth(d, locale) }
  const fmtFull = { format: (d: string | Date) => formatDayMonthYear(d, locale) }

  const fmtISO = (d: Date) => {
    const y = d.getFullYear()
    const m = `${d.getMonth() + 1}`.padStart(2, '0')
    const day = `${d.getDate()}`.padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const minDate = fmtISO(new Date())
  const maxDate = fmtISO(addDays(new Date(), 365))

  // Get cheapest available rate
  const cheapestRate = availability
    ? Math.min(...Object.values(availability).filter(a => a.available > 0).map(a => a.totalRate))
    : property.roomTypes.length > 0
      ? Math.min(...property.roomTypes.map(rt => rt.minRate))
      : null
  const currency = property.roomTypes[0]?.currency || 'SAR'

  return (
    <>
      {/* Desktop sticky sidebar */}
      <div id="booking-sidebar" className="hidden lg:block sticky top-24">
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-5 shadow-sm">
          {/* Price teaser */}
          {cheapestRate != null && cheapestRate !== Infinity && (
            <div className="mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('from')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {currency} {cheapestRate.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {availability ? `${t('forNights', { count: nights })}` : t('perNight')}
              </p>
            </div>
          )}

          {/* Date picker */}
          <div className="space-y-3">
            <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'flex items-center w-full h-11 px-3 rounded-md text-left text-sm',
                    'bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600',
                    'hover:border-gray-300 dark:hover:border-gray-500 transition-colors',
                    !checkIn && 'text-gray-400'
                  )}
                >
                  <CalendarIcon className="me-2 h-4 w-4 text-gray-400" />
                  {checkIn && checkOut ? (
                    <span className="text-gray-900 dark:text-gray-100">
                      {fmtShort.format(checkIn)} - {fmtShort.format(checkOut)}
                      <span className="text-gray-500 ms-1">({nights}n)</span>
                    </span>
                  ) : checkIn ? (
                    <span>{fmtFull.format(checkIn)} - ?</span>
                  ) : (
                    <span>{tSearch('selectDates')}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3 border-b">
                  <p className="text-sm text-muted-foreground">
                    {!checkIn ? tSearch('selectCheckIn') : !checkOut ? tSearch('selectCheckOut') : tSearch('clickToChangeDates')}
                  </p>
                </div>
                <Calendar
                  month={checkIn ? toLocalDate(checkIn) : new Date()}
                  rangeStart={checkIn}
                  rangeEnd={checkOut}
                  onChange={handleDateSelect}
                  minDate={minDate}
                  maxDate={maxDate}
                  locale={locale}
                />
              </PopoverContent>
            </Popover>

            {/* Guest selector */}
            <Popover open={isGuestOpen} onOpenChange={setIsGuestOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'flex items-center w-full h-11 px-3 rounded-md text-left text-sm',
                    'bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600',
                    'hover:border-gray-300 dark:hover:border-gray-500 transition-colors',
                    'text-gray-900 dark:text-gray-100'
                  )}
                >
                  <Users className="me-2 h-4 w-4 text-gray-400" />
                  {tSearch('guestCount', { count: totalGuests })}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="start">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{tSearch('adults')}</p>
                      <p className="text-sm text-muted-foreground">{tSearch('ages13Plus')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAdults(Math.max(1, adults - 1))} disabled={adults <= 1}>
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{adults}</span>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAdults(Math.min(10, adults + 1))} disabled={adults >= 10}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{tSearch('children')}</p>
                      <p className="text-sm text-muted-foreground">{tSearch('ages0to12')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setChildren(Math.max(0, children - 1))} disabled={children <= 0}>
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{children}</span>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setChildren(Math.min(10, children + 1))} disabled={children >= 10}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <Button className="w-full" onClick={() => setIsGuestOpen(false)}>{tSearch('done')}</Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Search button */}
            <Button
              className="w-full h-11"
              onClick={handleSearch}
              disabled={!checkIn || !checkOut || isSearching}
            >
              {isSearching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tSearch('searching')}
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  {t('checkAvailability')}
                </>
              )}
            </Button>

            {/* Promo code */}
            <div className="flex flex-col items-center gap-1.5">
              {!showPromoField ? (
                <button
                  type="button"
                  onClick={() => setShowPromoField(true)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 transition-colors flex items-center gap-1"
                >
                  <Tag className="h-3 w-3" />
                  {tSearch('havePromoCode')}
                </button>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 w-full">
                    <Tag className="h-3 w-3 text-gray-400 shrink-0" />
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => handleCouponChange(e.target.value)}
                        placeholder={tSearch('enterPromoCode')}
                        autoFocus
                        className={cn(
                          'w-full h-8 px-2 pe-7 text-xs uppercase rounded border outline-none transition-colors',
                          'bg-gray-50 dark:bg-gray-900/50 placeholder:text-gray-400',
                          'text-gray-900 dark:text-gray-100',
                          couponStatus === 'valid'
                            ? 'border-green-400 dark:border-green-600'
                            : (couponStatus === 'invalid' || couponStatus === 'not_eligible')
                              ? 'border-red-300 dark:border-red-600'
                              : 'border-gray-200 dark:border-gray-600 focus:border-gray-300 dark:focus:border-gray-500'
                        )}
                      />
                      <span className="absolute end-1.5 top-1/2 -translate-y-1/2">
                        {couponStatus === 'validating' && (
                          <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />
                        )}
                        {couponStatus === 'valid' && (
                          <Check className="h-3.5 w-3.5 text-green-500" />
                        )}
                        {(couponStatus === 'invalid' || couponStatus === 'not_eligible') && (
                          <AlertCircle className="h-3.5 w-3.5 text-red-400" />
                        )}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCouponCode(''); setCouponStatus('idle'); setCouponInfo(null); setShowPromoField(false) }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {/* Contextual banner — shows the specific reason
                      (stay window, min nights, property scope, etc.) */}
                  {couponEligibility && couponCode.length >= 3 && (
                    <CouponBanner
                      eligibility={couponEligibility}
                      preview={couponPreview ?? undefined}
                      size="compact"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-4 py-3 safe-area-bottom">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            {availability && searchedDates ? (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {fmtShort.format(searchedDates.checkIn)} – {fmtShort.format(searchedDates.checkOut)}
                  <span className="text-gray-500 ms-1">({nights}n)</span>
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                  {currency} {cheapestRate != null && cheapestRate !== Infinity ? cheapestRate.toFixed(2) : '—'}
                </p>
              </>
            ) : cheapestRate != null && cheapestRate !== Infinity ? (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('from')}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                  {currency} {cheapestRate.toFixed(2)}
                  <span className="text-xs font-normal text-gray-500 ms-1">/ {t('night')}</span>
                </p>
              </>
            ) : null}
          </div>
          <Button
            className="shrink-0"
            onClick={() => setIsMobileSheetOpen(true)}
          >
            {availability ? tSearch('modify') : t('checkAvailability')}
          </Button>
        </div>
      </div>

      {/* Mobile booking bottom sheet */}
      <div
        className={cn(
          'fixed inset-0 z-50 lg:hidden transition-opacity duration-200',
          isMobileSheetOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/40" onClick={() => setIsMobileSheetOpen(false)} />

        {/* Sheet */}
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl transition-transform duration-300 ease-out max-h-[90vh] flex flex-col',
            isMobileSheetOpen ? 'translate-y-0' : 'translate-y-full'
          )}
        >
          {/* Drag handle + header */}
          <div className="flex flex-col items-center pt-2 pb-3 px-4 border-b dark:border-gray-700 shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600 mb-3" />
            <div className="flex items-center justify-between w-full">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('checkAvailability')}</h3>
              <button
                type="button"
                onClick={() => setIsMobileSheetOpen(false)}
                aria-label="Close"
                className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 overscroll-contain">
            {/* Date selection hint */}
            <div className="px-4 pt-3 pb-1">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {!checkIn ? tSearch('selectCheckIn') : !checkOut ? tSearch('selectCheckOut') : tSearch('clickToChangeDates')}
              </p>
              {checkIn && checkOut && (
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                  {fmtFull.format(checkIn)} – {fmtFull.format(checkOut)}
                  <span className="text-gray-500 ms-1">({nights} {nights === 1 ? 'night' : 'nights'})</span>
                </p>
              )}
            </div>

            {/* Calendar */}
            <div className="px-2">
              <Calendar
                month={checkIn ? toLocalDate(checkIn) : new Date()}
                rangeStart={checkIn}
                rangeEnd={checkOut}
                onChange={handleDateSelect}
                minDate={minDate}
                maxDate={maxDate}
                locale={locale}
                fullWidth
              />
            </div>

            {/* Guest selector */}
            <div className="px-4 py-3 space-y-3 border-t dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{tSearch('adults')}</p>
                  <p className="text-xs text-gray-500">{tSearch('ages13Plus')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                    disabled={adults <= 1}
                    aria-label="Decrease adults"
                    className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center disabled:opacity-30"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-6 text-center font-medium text-gray-900 dark:text-gray-100">{adults}</span>
                  <button
                    type="button"
                    onClick={() => setAdults(Math.min(10, adults + 1))}
                    disabled={adults >= 10}
                    aria-label="Increase adults"
                    className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center disabled:opacity-30"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{tSearch('children')}</p>
                  <p className="text-xs text-gray-500">{tSearch('ages0to12')}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setChildren(Math.max(0, children - 1))}
                    disabled={children <= 0}
                    aria-label="Decrease children"
                    className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center disabled:opacity-30"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-6 text-center font-medium text-gray-900 dark:text-gray-100">{children}</span>
                  <button
                    type="button"
                    onClick={() => setChildren(Math.min(10, children + 1))}
                    disabled={children >= 10}
                    aria-label="Increase children"
                    className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center disabled:opacity-30"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Promo code (mobile) */}
            <div className="px-4 pb-3">
              {!showPromoField ? (
                <button
                  type="button"
                  onClick={() => setShowPromoField(true)}
                  className="text-xs text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors flex items-center gap-1"
                >
                  <Tag className="h-3 w-3" />
                  {tSearch('havePromoCode')}
                </button>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3 w-3 text-gray-400 shrink-0" />
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => handleCouponChange(e.target.value)}
                        placeholder={tSearch('enterPromoCode')}
                        className={cn(
                          'w-full h-9 px-3 pe-8 text-sm uppercase rounded-lg border outline-none transition-colors',
                          'bg-gray-50 dark:bg-gray-900/50 placeholder:text-gray-400',
                          'text-gray-900 dark:text-gray-100',
                          couponStatus === 'valid'
                            ? 'border-green-400 dark:border-green-600'
                            : (couponStatus === 'invalid' || couponStatus === 'not_eligible')
                              ? 'border-red-300 dark:border-red-600'
                              : 'border-gray-200 dark:border-gray-600 focus:border-gray-300'
                        )}
                      />
                      <span className="absolute end-2 top-1/2 -translate-y-1/2">
                        {couponStatus === 'validating' && <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />}
                        {couponStatus === 'valid' && <Check className="h-3.5 w-3.5 text-green-500" />}
                        {(couponStatus === 'invalid' || couponStatus === 'not_eligible') && <AlertCircle className="h-3.5 w-3.5 text-red-400" />}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCouponCode(''); setCouponStatus('idle'); setCouponInfo(null); setShowPromoField(false) }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Contextual banner — same component as desktop, mobile sizing */}
                  {couponEligibility && couponCode.length >= 3 && (
                    <CouponBanner
                      eligibility={couponEligibility}
                      preview={couponPreview ?? undefined}
                      size="compact"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Fixed search button at bottom */}
          <div className="px-4 py-3 border-t dark:border-gray-700 safe-area-bottom shrink-0">
            <Button
              className="w-full h-12 text-base"
              onClick={handleSearch}
              disabled={!checkIn || !checkOut || isSearching}
            >
              {isSearching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tSearch('searching')}
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  {t('checkAvailability')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
